/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * Playback listening-gain mapping (handoff v0.8.3.1 `playbackGainFromSlider`
 * / `formatPlaybackGain`): the 0–100 slider maps piecewise-linearly to a
 * 0–5× amplification with unity at the midpoint —
 *
 *   0..50  → 0..1×   (attenuation range)
 *   50..100 → 1..5×  (amplification range)
 *
 * so the default slider position (50) is exactly 1× and does not color the
 * signal. The gain drives a dedicated GainNode on the playback output only
 * (audio-graph.js setAmplification) and must never change DSP calculations,
 * waveform evidence, or exports.
 */

/** Slider midpoint = unity gain; the control's initial value. */
const DEFAULT_GAIN_SLIDER_VALUE = 50

const playbackGainFromSlider = (rawValue: unknown): number => {
  const value = Math.max(0, Math.min(100, Number(rawValue) || 0))
  return value <= 50 ? value / 50 : 1 + ((value - 50) / 50) * 4
}

/** "×1", "×2.5", "×0" — the compact control's gain readout. */
const formatPlaybackGain = (gain: number): string => {
  if (gain === 0) {
    return "×0"
  }
  const rounded = Math.round(gain * 10) / 10
  return `×${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}`
}

/** Speaker-glyph bucket for the mute toggle's icon (0 → muted glyph). */
const speakerIconFor = (audibleGain: number): "speaker-muted" | "speaker-low" | "speaker-medium" | "speaker-high" => {
  if (audibleGain === 0) {
    return "speaker-muted"
  }
  return audibleGain <= 0.5 ? "speaker-low" : audibleGain <= 1 ? "speaker-medium" : "speaker-high"
}

export { DEFAULT_GAIN_SLIDER_VALUE, playbackGainFromSlider, formatPlaybackGain, speakerIconFor }
